// B-77 — the state behind the Cierres bell: the signed-in person's own "Procesar el día"
// notifications, how many are unread, and marking them read.
//
// Out of the component so it can be tested through a probe without Radix. Three rules:
//
// - It asks only while the tab is visible — once on mount, every `INTERVALO_AVISOS_MS`, again when
//   the tab comes back — and at once when `refrescar` changes (the shell passes `diaEnCurso.fin`,
//   so the notification is there the moment the chip says the day ended).
// - A failed read changes nothing on screen: the bell keeps what it had and the reason goes to the
//   console. A backend without the route (deployed separately) is just a bell with no dot.
// - Marking is optimistic and then confirmed by the next read; a failed mark is retried by it.

import { useCallback, useEffect, useRef, useState } from "react";

import { listCierreRunNotifications, markCierreRunNotificationsRead } from "../../../ApiCalls/mantisBusiness";

export const INTERVALO_AVISOS_MS = 60 * 1000;

const visible = () => typeof document === "undefined" || document.visibilityState !== "hidden";

export function useAvisosDeProceso({ token, activo = true, refrescar = 0 }) {
  const [avisos, setAvisos] = useState([]);
  const [noLeidos, setNoLeidos] = useState(0);
  const montado = useRef(true);
  // Only the latest read may land: a slow answer must not overwrite a newer one.
  const turno = useRef(0);

  // Set on every mount, not only the first: React 18's StrictMode mounts, unmounts and mounts again
  // in development, and a flag left `false` by that first cleanup would drop every answer after it.
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const cargar = useCallback(async () => {
    if (!activo || !visible()) return;
    const mio = ++turno.current;
    // Through a promise, so a call that throws before returning one is caught the same way.
    const response = await Promise.resolve()
      .then(() => listCierreRunNotifications(token))
      .catch((error) => {
        console.warn("[cierres avisos] no se pudieron leer", error);
        return null;
      });
    if (!montado.current || mio !== turno.current || !response?.success) return;
    setAvisos(response.data?.notifications || []);
    setNoLeidos(response.data?.unread || 0);
  }, [token, activo]);

  useEffect(() => {
    if (!activo) return undefined;
    cargar();
    const intervalo = setInterval(cargar, INTERVALO_AVISOS_MS);
    const alVolver = () => {
      if (visible()) cargar();
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      clearInterval(intervalo);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [activo, cargar, refrescar]);

  const marcar = useCallback(
    async (ids) => {
      const todos = !ids;
      setAvisos((actual) => actual.map((a) => (todos || ids.includes(a.id) ? { ...a, read: true } : a)));
      setNoLeidos((n) => (todos ? 0 : Math.max(n - ids.filter((id) => avisos.some((a) => a.id === id && !a.read)).length, 0)));
      await Promise.resolve()
        .then(() => markCierreRunNotificationsRead(token, ids))
        .catch((error) => {
          console.warn("[cierres avisos] no se pudieron marcar", error);
        });
      cargar();
    },
    [token, avisos, cargar]
  );

  return { avisos, noLeidos, marcar, recargar: cargar };
}
